import type { ReactNode, Ref } from "react";
import type {
	InputProps as AriaInputProps,
	NumberFieldProps as AriaNumberFieldProps,
} from "react-aria-components";

import { ChevronDown, ChevronUp, Minus, Plus } from "@untitledui/icons";
import { createContext, useContext } from "react";
import {
	Button as AriaButton,
	Group as AriaGroup,
	Input as AriaInput,
	NumberField as AriaNumberField,
} from "react-aria-components";

import { Button } from "@/components/ui/buttons/button";
import { HintText } from "@/components/ui/input/hint-text";
import { Label } from "@/components/ui/input/label";
import { cx } from "@/utils/cx";

const NumberFieldContext = createContext<{
	iconClassName?: string;
	inputClassName?: string;
	size?: "lg" | "md" | "sm";
	tooltipClassName?: string;
	wrapperClassName?: string;
}>({});

const styles = {
	lg: "px-3.5 py-2.5 text-md",
	md: "px-3 py-2 text-md",
	sm: "px-3 py-2 text-sm",
};

export interface InputNumberBaseProps extends AriaNumberFieldProps {
	groupRef?: Ref<HTMLDivElement>;
	/** Class name for the input. */
	inputClassName?: string;
	/** Orientation of buttons. */
	orientation?: "horizontal" | "vertical";
	/** Placeholder text. */
	placeholder?: string;
	ref?: Ref<HTMLInputElement>;
	/**
	 * Input size.
	 * @default "sm"
	 */
	size?: "lg" | "md" | "sm";
	/** Class name for the input wrapper. */
	wrapperClassName?: string;
}

export const InputNumberBase = ({
	groupRef,
	inputClassName,
	isDisabled,
	isInvalid,
	// Omit this prop to avoid invalid HTML attribute warning
	isRequired: _isRequired,
	orientation = "vertical",
	placeholder,
	ref,
	size = "sm",
	wrapperClassName,
	...inputProps
}: Omit<InputNumberBaseProps, "hint" | "label">) => {
	const context = useContext(NumberFieldContext);
	const inputSize = context?.size || size;

	return (
		<AriaGroup
			{...{ isDisabled, isInvalid }}
			className={({ isDisabled, isFocusWithin, isInvalid }) =>
				cx(
					"relative flex w-full flex-row items-stretch rounded-lg bg-primary shadow-xs outline-1 -outline-offset-1 outline-primary transition-all duration-100 ease-linear",

					isFocusWithin &&
						!isDisabled &&
						"outline-2 -outline-offset-2 outline-brand",

					// Disabled state styles
					isDisabled &&
						"cursor-not-allowed opacity-50 in-data-input-wrapper:opacity-100",
					"group-disabled:cursor-not-allowed group-disabled:opacity-50 group-disabled:in-data-input-wrapper:opacity-100",

					// Invalid state styles
					isInvalid && "outline-error_subtle",
					"group-invalid:outline-error_subtle",

					// Invalid state with focus-within styles
					isInvalid &&
						isFocusWithin &&
						"outline-2 -outline-offset-2 outline-error",
					isFocusWithin &&
						"group-invalid:outline-2 group-invalid:-outline-offset-2 group-invalid:outline-error",

					context?.wrapperClassName,
					wrapperClassName,
				)
			}
			ref={groupRef}
		>
			{orientation === "horizontal" && (
				<Button
					className="static h-full rounded-r-none"
					color="tertiary"
					iconLeading={Minus}
					size={size}
					slot="decrement"
				/>
			)}

			{/* Input field */}
			<AriaInput
				{...(inputProps as AriaInputProps)}
				className={cx(
					"m-0 w-full bg-transparent text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary disabled:cursor-not-allowed",
					orientation === "horizontal" && "text-center",
					styles[inputSize],
					context?.inputClassName,
					inputClassName,
				)}
				placeholder={placeholder}
				ref={ref}
			/>

			{orientation === "horizontal" && (
				<Button
					className="static h-full rounded-l-none"
					color="tertiary"
					iconLeading={Plus}
					size={size}
					slot="increment"
				/>
			)}

			{orientation === "vertical" && (
				<div
					className={cx(
						"flex w-7 shrink-0 flex-col border-l border-primary",
						size === "lg" && "w-7.5",
					)}
				>
					<AriaButton
						className="flex flex-1 cursor-pointer items-center justify-center text-fg-quaternary outline-brand transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover disabled:cursor-not-allowed disabled:opacity-50"
						slot="increment"
					>
						<ChevronUp
							className={cx(
								"size-3 stroke-3",
								size === "lg" && "size-3.5 stroke-[2.57px]",
							)}
						/>
					</AriaButton>
					<AriaButton
						className="flex flex-1 cursor-pointer items-center justify-center border-t border-primary text-fg-quaternary outline-brand transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover disabled:cursor-not-allowed disabled:opacity-50"
						slot="decrement"
					>
						<ChevronDown
							className={cx(
								"size-3 stroke-3",
								size === "lg" && "size-3.5 stroke-[2.57px]",
							)}
						/>
					</AriaButton>
				</div>
			)}
		</AriaGroup>
	);
};

InputNumberBase.displayName = "InputNumberBase";

interface InputNumberProps extends InputNumberBaseProps {
	/** Whether to hide required indicator from label */
	hideRequiredIndicator?: boolean;
	/** Helper text displayed below the input */
	hint?: ReactNode;
	/** Label text for the input */
	label?: string;
}

export const InputNumber = ({
	className,
	groupRef,
	hideRequiredIndicator,
	hint,
	inputClassName,
	label,
	orientation = "vertical",
	placeholder,
	ref,
	size = "sm",
	wrapperClassName,
	...props
}: InputNumberProps) => {
	return (
		<NumberFieldContext.Provider
			value={{ inputClassName, size, wrapperClassName }}
		>
			<AriaNumberField
				{...props}
				className={(state) =>
					cx(
						"group flex h-max w-auto flex-col items-start justify-start gap-1.5",
						typeof className === "function" ? className(state) : className,
					)
				}
			>
				{({ isInvalid, isRequired }) => (
					<>
						{label && (
							<Label
								isRequired={
									hideRequiredIndicator ? !hideRequiredIndicator : isRequired
								}
							>
								{label}
							</Label>
						)}

						<InputNumberBase
							{...{
								groupRef,
								inputClassName,
								orientation,
								placeholder,
								ref,
								size,
								wrapperClassName,
							}}
						/>

						{hint && (
							<HintText
								className={cx(size === "sm" && "text-xs")}
								isInvalid={isInvalid}
							>
								{hint}
							</HintText>
						)}
					</>
				)}
			</AriaNumberField>
		</NumberFieldContext.Provider>
	);
};

InputNumber.displayName = "InputNumber";
